import Button from "@mui/material/Button";
import Collapse from "@mui/material/Collapse";
import FormControlLabel from "@mui/material/FormControlLabel";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";
import { MouseEvent, useEffect, useState } from "react";

import { routers } from "@/configs";
import { useDeviceType, useShowFooter, useTranslate } from "@/hooks";
import {
  onCookieSettingsOpen,
  readCookieConsent,
  saveCookieConsent,
  useCookieConsent,
} from "@/lib/cookieConsent";
import routes from "@/routes";

/** Height of the phone's bottom navigation, which the banner must clear. */
const MOBILE_FOOTER_OFFSET = 64;

const CATEGORIES = ["analytics", "marketing"] as const;

// Shown until answered; the Google tag (lib/gtag.ts) follows the choice, any other script must check hasCookieConsent().
const CookieConsentBanner = () => {
  const t = useTranslate();
  const consent = useCookieConsent();
  const showMobileFooter = useShowFooter();
  const { isMobile } = useDeviceType();

  const [reopened, setReopened] = useState(false);
  const [customising, setCustomising] = useState(false);
  const [choice, setChoice] = useState({ analytics: false, marketing: false });

  useEffect(
    () =>
      onCookieSettingsOpen(() => {
        const stored = readCookieConsent();
        setChoice({ analytics: !!stored?.analytics, marketing: !!stored?.marketing });
        setCustomising(true);
        setReopened(true);
      }),
    [],
  );

  if (consent && !reopened) return null;

  const decide = (next: { analytics: boolean; marketing: boolean }) => {
    saveCookieConsent(next);
    setReopened(false);
    setCustomising(false);
  };

  const openPolicy = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    void routes.navigate(routers.CookiePolicy);
  };

  const bottomOffset =
    isMobile && showMobileFooter
      ? `calc(${MOBILE_FOOTER_OFFSET}px + env(safe-area-inset-bottom, 0px))`
      : "16px";

  return (
    <Paper
      role="region"
      aria-label={t("cookie-consent.title")}
      elevation={8}
      sx={{
        position: "fixed",
        zIndex: (theme) => theme.zIndex.drawer + 50,
        left: 16,
        right: { xs: 16, sm: "auto" },
        bottom: bottomOffset,
        maxWidth: { sm: 520 },
        maxHeight: "70vh",
        overflowY: "auto",
        p: 2,
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Stack gap={1.5}>
        <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 700 }}>
          {t("cookie-consent.title")}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t("cookie-consent.body")}{" "}
          <Link href={routers.CookiePolicy} onClick={openPolicy}>
            {t("cookie-consent.policy-link")}
          </Link>
        </Typography>

        <Collapse in={customising} unmountOnExit>
          <Stack gap={1}>
            <FormControlLabel
              control={<Switch checked disabled />}
              label={
                <Stack>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {t("cookie-consent.essential-label")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t("cookie-consent.essential-desc")}
                  </Typography>
                </Stack>
              }
            />
            {CATEGORIES.map((category) => (
              <FormControlLabel
                key={category}
                control={
                  <Switch
                    checked={choice[category]}
                    onChange={(event) =>
                      setChoice((prev) => ({ ...prev, [category]: event.target.checked }))
                    }
                  />
                }
                label={
                  <Stack>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {t(`cookie-consent.${category}-label`)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t(`cookie-consent.${category}-desc`)}
                    </Typography>
                  </Stack>
                }
              />
            ))}
          </Stack>
        </Collapse>

        <Stack direction={{ xs: "column", sm: "row" }} gap={1} justifyContent="flex-end">
          {customising ? (
            <Button variant="outlined" onClick={() => decide(choice)}>
              {t("cookie-consent.save")}
            </Button>
          ) : (
            <Button variant="text" onClick={() => setCustomising(true)}>
              {t("cookie-consent.customise")}
            </Button>
          )}
          <Button variant="outlined" onClick={() => decide({ analytics: false, marketing: false })}>
            {t("cookie-consent.essential-only")}
          </Button>
          <Button variant="contained" onClick={() => decide({ analytics: true, marketing: true })}>
            {t("cookie-consent.accept-all")}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
};

export default CookieConsentBanner;
